import React, { useEffect, useState } from 'react';
import { ChevronRight, ThumbsUp } from 'lucide-react';
import { newsFeedAPI, API_URL } from '../../../lib/api';
import { CompanyAvatar, relativeDate } from '../../news/NewsPostHeader';

const absUrl = (u) => (u && u.startsWith('/') ? `${API_URL}${u}` : u);

export const QuickAccessGrid = ({ items }) => (
  <section className="mx-4 bg-white rounded-3xl border border-slate-200 p-4" data-testid="quick-access-grid">
    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-3">Acesso rápido</p>
    <div className="grid grid-cols-4 gap-y-4 gap-x-2">
      {items.map((q) => (
        <button key={q.label} type="button" onClick={q.onClick} data-tour={q.tour} data-testid={q.testId}
          className="flex flex-col items-center gap-1.5 active:scale-95 transition-transform">
          <div className={`relative w-14 h-14 rounded-2xl flex items-center justify-center ${q.color}`}>
            <q.icon className="w-6 h-6" />
            {q.badge > 0 && (
              <span className="absolute -top-1 -right-1 min-w-5 h-5 px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">
                {q.badge > 9 ? '9+' : q.badge}
              </span>
            )}
            {q.isNew && <span className="absolute -top-1.5 -right-2 px-1.5 rounded-full bg-emerald-600 text-white text-[9px] font-bold">NOVO</span>}
          </div>
          <span className="text-[11px] font-medium text-slate-700 text-center leading-tight">{q.label}</span>
        </button>
      ))}
    </div>
  </section>
);

export const AlertsCarousel = ({ alerts }) => {
  if (!alerts.length) return null;
  return (
    <section className="px-4" data-testid="home-alerts-carousel">
      <div className="flex gap-3 overflow-x-auto snap-x pb-1" style={{ scrollbarWidth: 'none' }}>
        {alerts.map((a) => (
          <button key={a.id} type="button" onClick={a.onClick} data-testid={`home-alert-${a.id}`}
            className={`snap-start shrink-0 ${alerts.length > 1 ? 'w-[85%]' : 'w-full'} flex items-center gap-3 p-3 rounded-2xl border text-left ${a.cls}`}>
            <a.icon className="w-5 h-5 shrink-0" />
            <span className="flex-1 min-w-0">
              <span className="block text-sm font-semibold leading-tight line-clamp-2">{a.title}</span>
              {a.sub && <span className="block text-[11px] opacity-80 truncate">{a.sub}</span>}
            </span>
            <span className={`shrink-0 text-[11px] text-white px-2.5 py-1 rounded-full ${a.btn}`}>{a.cta}</span>
          </button>
        ))}
      </div>
    </section>
  );
};

export const FeedPreview = ({ isEmployee, onOpenAll, onOpen, onUnread }) => {
  const [posts, setPosts] = useState(null);
  useEffect(() => {
    (isEmployee ? newsFeedAPI.getEmployeeFeed() : newsFeedAPI.getCandidateFeed())
      .then((r) => {
        const list = r.data?.news || [];
        setPosts(list.slice(0, 3));
        onUnread(list.filter((n) => !n.lido).length);
      })
      .catch(() => setPosts([]));
  }, [isEmployee]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!posts || posts.length === 0) return null;
  return (
    <section data-testid="home-feed-preview">
      <div className="flex items-center justify-between px-4 mb-2">
        <p className="text-sm font-bold text-slate-900">Últimos comunicados</p>
        <button type="button" onClick={onOpenAll} className="text-xs text-cyan-700 flex items-center" data-testid="home-feed-see-all">Ver todos <ChevronRight className="w-3.5 h-3.5" /></button>
      </div>
      <div className="flex gap-3 overflow-x-auto px-4 pb-1 snap-x" style={{ scrollbarWidth: 'none' }}>
        {posts.map((p) => (
          <button key={p.id} type="button" onClick={() => onOpen(p)} data-testid={`home-feed-post-${p.id}`}
            className="snap-start w-64 shrink-0 text-left bg-white rounded-2xl border border-slate-200 overflow-hidden">
            <div className="flex items-center gap-2 px-3 py-2">
              <CompanyAvatar name={p.empresa_nome} logo={p.empresa_logo} size="w-7 h-7" />
              <div className="min-w-0">
                <p className="text-xs font-semibold text-slate-900 truncate">{p.empresa_nome || 'Empresa'}</p>
                <p className="text-[10px] text-slate-500">{relativeDate(p.data_publicacao)}</p>
              </div>
              {!p.lido && <span className="ml-auto w-2 h-2 rounded-full bg-cyan-500" />}
            </div>
            <div className="aspect-video bg-gradient-to-br from-cyan-100 to-teal-100">
              {p.imagem_capa && <img src={absUrl(p.imagem_capa)} alt="" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />}
            </div>
            <div className="px-3 py-2">
              <p className="text-sm font-semibold text-slate-900 truncate">{p.titulo}</p>
              <p className="text-[11px] text-slate-500 flex items-center gap-1 mt-0.5"><ThumbsUp className="w-3.5 h-3.5" /> {p.total_reacoes || 0} {(p.total_reacoes || 0) === 1 ? 'reação' : 'reações'}</p>
            </div>
          </button>
        ))}
      </div>
    </section>
  );
};
